import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { startSyncLoop } from './offline/sync';
import { UpdateBanner } from './components/UpdateBanner';
import { startUpdateWatch } from './offline/updates';
import { envProblems } from './lib/env';
import { LoginScreen } from './screens/LoginScreen';
import { ChangePasswordScreen } from './screens/ChangePasswordScreen';
import { TurfListScreen } from './screens/TurfListScreen';
import { TurfLayout } from './screens/TurfLayout';
import { ListScreen } from './screens/ListScreen';
import { SyncScreen } from './screens/SyncScreen';
import { TutorialsScreen } from './screens/TutorialsScreen';

// MapLibre is by far the largest dependency in the bundle. Splitting it out
// keeps login and the turf list quick on a slow connection (PLAN 13.9); the
// map chunk then loads once and is cached.
const MapScreen = lazy(() =>
  import('./screens/MapScreen').then((m) => ({ default: m.MapScreen })),
);
const OrganizeScreen = lazy(() =>
  import('./screens/OrganizeScreen').then((m) => ({ default: m.OrganizeScreen })),
);
const ReportsScreen = lazy(() =>
  import('./screens/ReportsScreen').then((m) => ({ default: m.ReportsScreen })),
);
const PeopleScreen = lazy(() =>
  import('./screens/PeopleScreen').then((m) => ({ default: m.PeopleScreen })),
);
import { Toast } from './components/Toast';

function SetupScreen() {
  return (
    <main className="page">
      <h1>Configuration needed</h1>
      <ul className="setup">
        {envProblems.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </main>
  );
}

function Shell() {
  const { session, profile, loading, error, mustChangePassword } = useAuth();

  // Started once a canvasser is signed in and torn down on sign-out: the push
  // needs a session, and the pull is scoped to their assignments (PLAN 5.3).
  // It lives above the routes so walking from map to list never interrupts an
  // upload in flight.
  const signedIn = Boolean(session);
  useEffect(() => {
    if (!signedIn) return;
    return startSyncLoop();
  }, [signedIn]);

  if (loading) return <main className="page"><p className="muted">Loading…</p></main>;

  if (!session) return <LoginScreen />;
  if (error) return <main className="page"><p className="banner banner--error">{error}</p></main>;
  if (!profile || mustChangePassword === null) {
    return <main className="page"><p className="muted">Loading your profile…</p></main>;
  }
  // Signed in on a password an organizer reset and read out. Nothing else is
  // reachable until they replace it (20260927000100).
  if (mustChangePassword) return <ChangePasswordScreen />;

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to="/turfs" replace />} />
        <Route path="/turfs" element={<TurfListScreen />} />
        <Route path="/sync" element={<SyncScreen />} />
        <Route path="/tutorials" element={<TutorialsScreen />} />
        <Route
          path="/organize"
          element={
            <Suspense fallback={<p className="muted map-loading">Loading…</p>}>
              <OrganizeScreen />
            </Suspense>
          }
        />
        <Route
          path="/reports"
          element={
            <Suspense fallback={<p className="muted map-loading">Loading…</p>}>
              <ReportsScreen />
            </Suspense>
          }
        />
        <Route
          path="/voters"
          element={
            <Suspense fallback={<p className="muted map-loading">Loading…</p>}>
              <PeopleScreen />
            </Suspense>
          }
        />
        <Route path="/turf/:turfId" element={<TurfLayout />}>
          <Route
            index
            element={
              <Suspense fallback={<p className="muted map-loading">Loading map…</p>}>
                <MapScreen />
              </Suspense>
            }
          />
          <Route path="list" element={<ListScreen />} />
        </Route>
        <Route path="*" element={<Navigate to="/turfs" replace />} />
      </Routes>
      <UpdateBanner />
      <Toast />
    </>
  );
}

// Registered once, outside React, so a strict-mode remount does not register
// the worker twice.
startUpdateWatch();

export function App() {
  if (envProblems.length > 0) return <SetupScreen />;
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}
